import { HotTakesStatementCard } from "@/components/shared/hot-takes-statement-card";
import { HotTakesSummaryCard } from "@/components/shared/hot-takes-summary-card";
import { useActivity } from "@/lib/api/activities";
import { useShareableEntity } from "@/lib/api/shareable-entities";
import { cn } from "@/lib/utils";
import type {
  HotTakeAnswer,
  HotTakesConfigStatement,
  HotTakesResponse,
} from "@somethings-ui/core/hot-takes";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { InteractionFooter } from "../interactions/interaction-footer";
import { useEntityInteractionHandlers } from "../interactions/use-entity-interaction-handlers";

interface ViewHotTakesViewProps {
  entityId: string;
  mentorName?: string | null;
  teenName: string;
  mentorId?: string;
  menteeId?: string;
  activeTab?: "new" | "all";
  onNavigateToList?: () => void;
  readOnly?: boolean;
  initialIndex?: number | null;
  mentorAvatarUrl?: string | null;
  teenAvatarUrl?: string | null;
}

type Card =
  | { kind: "summary" }
  | { kind: "statement"; statement: HotTakesConfigStatement };

export function ViewHotTakesView({
  entityId,
  mentorName,
  teenName,
  mentorId,
  menteeId,
  activeTab,
  onNavigateToList,
  readOnly = false,
  initialIndex,
  mentorAvatarUrl = null,
  teenAvatarUrl = null,
}: ViewHotTakesViewProps) {
  const { data: activity, isLoading } = useActivity(entityId);
  const { data: entity } = useShareableEntity(entityId, menteeId);

  const teenShare = entity?.shares.find(
    (share) => share.sharedWith.id === menteeId,
  );
  const resolvedMentorAvatarUrl =
    entity && entity.owner.id === mentorId
      ? (entity.owner.profile_picture_url ?? mentorAvatarUrl)
      : mentorAvatarUrl;
  const resolvedTeenAvatarUrl =
    teenShare?.sharedWith.profile_picture_url ?? teenAvatarUrl;

  const config = activity?.config as
    | { statements?: HotTakesConfigStatement[] }
    | undefined;
  const result = activity?.result as {
    responses?: HotTakesResponse[];
    matchesCount?: number;
  } | null;

  const statements = useMemo(() => config?.statements ?? [], [config]);
  const matchesCount = Number(result?.matchesCount ?? 0);
  const teenAnswerById = useMemo(
    () =>
      new Map<string, HotTakeAnswer>(
        (result?.responses ?? []).map((r) => [r.statementId, r.teenAnswer]),
      ),
    [result?.responses],
  );

  // Cards: index 0 = summary, indices 1..N = each statement.
  const cards = useMemo<Card[]>(
    () => [
      { kind: "summary" as const },
      ...statements.map((s) => ({ kind: "statement" as const, statement: s })),
    ],
    [statements],
  );

  const initialCardIndex = useMemo(() => {
    const idx = initialIndex ?? 0;
    return idx >= 0 && idx < cards.length ? idx : 0;
  }, [initialIndex, cards.length]);

  const [currentIndex, setCurrentIndex] = useState(initialCardIndex);

  // If the activity loads after mount (statements arrive late), re-anchor the
  // carousel to the requested initial card once.
  const haveAnchoredRef = useRef(false);
  useEffect(() => {
    if (haveAnchoredRef.current) return;
    if (statements.length === 0) return;
    haveAnchoredRef.current = true;
    setCurrentIndex(initialCardIndex);
  }, [statements.length, initialCardIndex]);

  const goPrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const goNext = () =>
    setCurrentIndex((i) => Math.min(cards.length - 1, i + 1));

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <p className="text-sm text-zinc-400">Hot Takes not found</p>
      </div>
    );
  }

  const currentCard = cards[currentIndex];

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="relative flex flex-1 items-center justify-center overflow-y-auto p-6">
        {/* Card stage */}
        <div className="flex items-center justify-center">
          {currentCard?.kind === "summary" ? (
            <HotTakesSummaryCard
              mentorName={mentorName}
              teenName={teenName}
              matchesCount={matchesCount}
              mentorAvatarUrl={resolvedMentorAvatarUrl}
              teenAvatarUrl={resolvedTeenAvatarUrl}
              statements={statements}
              teenAnswerById={teenAnswerById}
            />
          ) : currentCard?.kind === "statement" ? (
            <HotTakesStatementCard
              statement={currentCard.statement}
              teenAnswer={teenAnswerById.get(currentCard.statement.id) ?? null}
              mentorName={mentorName || "Mentor"}
              teenName={teenName || "Teen"}
              mentorAvatarUrl={resolvedMentorAvatarUrl}
              teenAvatarUrl={resolvedTeenAvatarUrl}
            />
          ) : null}
        </div>

        {/* Prev / Next arrows */}
        {cards.length > 1 && (
          <>
            <CarouselButton
              direction="prev"
              disabled={currentIndex === 0}
              onClick={goPrev}
            />
            <CarouselButton
              direction="next"
              disabled={currentIndex === cards.length - 1}
              onClick={goNext}
            />
          </>
        )}

        {/* Pagination dots */}
        {cards.length > 1 && (
          <div className="pointer-events-none absolute bottom-4 left-0 right-0 flex justify-center">
            <PaginationDots current={currentIndex} total={cards.length} />
          </div>
        )}
      </div>

      {/* Bottom action bar */}
      {entity && menteeId && !readOnly && (
        <HotTakesActionBar
          entity={entity}
          menteeId={menteeId}
          activeTab={activeTab}
          onNavigateToList={onNavigateToList}
          teenName={teenName}
          index={currentIndex}
        />
      )}
    </div>
  );
}

function CarouselButton({
  direction,
  disabled,
  onClick,
}: {
  direction: "prev" | "next";
  disabled: boolean;
  onClick: () => void;
}) {
  const Icon = direction === "prev" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === "prev" ? "Previous take" : "Next take"}
      className={cn(
        "absolute top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md transition",
        direction === "prev" ? "left-4" : "right-4",
        "hover:bg-white disabled:cursor-not-allowed disabled:opacity-30",
      )}
    >
      <Icon className="h-5 w-5 text-zinc-800" />
    </button>
  );
}

function PaginationDots({
  current,
  total,
}: {
  current: number;
  total: number;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }, (_, i) => {
        const isActive = i === current;
        return (
          <span
            key={i}
            className={cn(
              "rounded-full transition-all",
              isActive ? "h-2 w-2 bg-zinc-800" : "h-1.5 w-1.5 bg-zinc-300",
            )}
          />
        );
      })}
    </div>
  );
}

type EntityArg = NonNullable<ReturnType<typeof useShareableEntity>["data"]>;

interface InteractionSectionProps {
  entity: EntityArg;
  menteeId: string;
  activeTab?: "new" | "all";
  onNavigateToList?: () => void;
  teenName: string;
  index: number;
}

function HotTakesActionBar({
  entity,
  menteeId,
  activeTab,
  onNavigateToList,
  teenName,
  index,
}: InteractionSectionProps) {
  const {
    isRead,
    isInteractionPending,
    handleReaction,
    handleComment,
    toggleReadStatus,
  } = useEntityInteractionHandlers({
    entity,
    menteeId,
    entityType: "hot_takes",
    activeTab,
    onNavigateToList,
  });

  // Intentionally NO isFromCurrentUser guard — hot_takes is asymmetric:
  // mentor sent the activity but the teen's answers are new content the
  // mentor should be able to react to / comment on.

  const previewThumbnail = (
    <div
      className="h-12 w-12 flex-shrink-0 rounded-md"
      style={{
        backgroundImage: "linear-gradient(180deg, #322964 0%, #1D1F46 100%)",
      }}
    />
  );

  return (
    <InteractionFooter
      ownerName={entity.owner?.name ?? teenName}
      isRead={isRead}
      isPending={isInteractionPending}
      previewThumbnail={previewThumbnail}
      onReaction={(emoji) => handleReaction(emoji, { index })}
      onComment={(text) => handleComment(text, { index })}
      onMarkAsRead={toggleReadStatus}
    />
  );
}
